import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Mail, Plus, Edit2, Trash2, Send, Clock, CheckCircle, 
  Users, Eye, MousePointer, FileText
} from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";

export default function AdminNewsletters() {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [sending, setSending] = useState(false);
  
  const [formData, setFormData] = useState({
    subject: "",
    content: "",
    scheduled_at: ""
  });

  useEffect(() => {
    fetchNewsletters();
  }, []);

  const fetchNewsletters = async () => {
    try {
      const response = await axios.get(`${API}/newsletters`, { withCredentials: true });
      setNewsletters(response.data);
    } catch (error) {
      console.error("Error fetching newsletters:", error);
      toast.error("Failed to load newsletters");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (isEditing) {
        await axios.put(`${API}/newsletters/${selectedNewsletter.newsletter_id}`, formData, { withCredentials: true });
        toast.success("Newsletter updated successfully!");
      } else {
        await axios.post(`${API}/newsletters`, formData, { withCredentials: true });
        toast.success("Newsletter created successfully!");
      }
      setDialogOpen(false);
      resetForm();
      fetchNewsletters();
    } catch (error) {
      console.error("Error saving newsletter:", error);
      toast.error("Failed to save newsletter");
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/newsletters/${selectedNewsletter.newsletter_id}/send`, 
        {}, 
        { withCredentials: true }
      );
      toast.success(`Newsletter sent to ${response.data.recipient_count} subscribers!`);
      setSendDialogOpen(false);
      fetchNewsletters();
    } catch (error) {
      console.error("Error sending newsletter:", error);
      toast.error("Failed to send newsletter");
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/newsletters/${selectedNewsletter.newsletter_id}`, { withCredentials: true });
      toast.success("Newsletter deleted");
      setDeleteDialogOpen(false);
      fetchNewsletters();
    } catch (error) {
      console.error("Error deleting newsletter:", error);
      toast.error("Failed to delete newsletter");
    }
  };

  const openEditDialog = (newsletter) => {
    setSelectedNewsletter(newsletter);
    setFormData({
      subject: newsletter.subject,
      content: newsletter.content || "",
      scheduled_at: newsletter.scheduled_at || ""
    });
    setIsEditing(true);
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsEditing(false);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      subject: "",
      content: "",
      scheduled_at: ""
    });
    setSelectedNewsletter(null);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "sent":
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Sent</Badge>;
      case "scheduled":
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" /> Scheduled</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700"><FileText className="w-3 h-3 mr-1" /> Draft</Badge>;
    }
  };

  const draftCount = newsletters.filter(n => n.status === "draft").length;
  const sentCount = newsletters.filter(n => n.status === "sent").length;
  const totalRecipients = newsletters.reduce((sum, n) => sum + (n.recipient_count || 0), 0);

  return (
    <AdminLayout currentPage="Newsletters">
      <div className="p-8" data-testid="admin-newsletters">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-heading-2 text-deep-blue">Newsletter Campaigns</h1>
            <p className="text-slate-600">Create and send newsletters to your subscribers</p>
          </div>
          <Button className="btn-primary" onClick={openCreateDialog} data-testid="create-newsletter-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Newsletter
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-awareness-blue/10 rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5 text-awareness-blue" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Newsletters</p>
                <p className="text-2xl font-bold text-deep-blue">{newsletters.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <FileText className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Drafts</p>
                <p className="text-2xl font-bold text-deep-blue">{draftCount}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Sent</p>
                <p className="text-2xl font-bold text-deep-blue">{sentCount}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Sent To</p>
                <p className="text-2xl font-bold text-deep-blue">{totalRecipients}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletters List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : newsletters.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">Subject</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Status</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Recipients</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Date</th>
                  <th className="text-right p-4 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {newsletters.map(newsletter => (
                  <tr key={newsletter.newsletter_id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="p-4">
                      <div className="font-medium text-deep-blue">{newsletter.subject}</div>
                      <div className="text-sm text-slate-500 truncate max-w-md">
                        {newsletter.content?.substring(0, 80)}...
                      </div>
                    </td>
                    <td className="p-4">{getStatusBadge(newsletter.status)}</td>
                    <td className="p-4 text-slate-600">
                      {newsletter.status === "sent" ? newsletter.recipient_count : "-"}
                    </td>
                    <td className="p-4 text-slate-600">
                      {newsletter.sent_at 
                        ? format(new Date(newsletter.sent_at), "MMM d, yyyy h:mm a")
                        : format(new Date(newsletter.created_at), "MMM d, yyyy")
                      }
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        {newsletter.status !== "sent" && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditDialog(newsletter)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-green-600 border-green-600 hover:bg-green-50"
                              onClick={() => { setSelectedNewsletter(newsletter); setSendDialogOpen(true); }}
                            >
                              <Send className="w-4 h-4 mr-1" /> Send
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => { setSelectedNewsletter(newsletter); setDeleteDialogOpen(true); }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {newsletter.status === "sent" && (
                          <span className="text-sm text-slate-400">Sent</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg p-12 text-center">
            <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No newsletters yet</h3>
            <p className="text-slate-500 mb-4">Create your first newsletter to engage with your supporters</p>
            <Button className="btn-primary" onClick={openCreateDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Create Newsletter
            </Button>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>{isEditing ? "Edit Newsletter" : "Create Newsletter"}</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="subject">Subject Line *</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  placeholder="Monthly Update: Prostate Cancer Awareness News"
                  required
                  data-testid="newsletter-subject-input"
                />
              </div>

              <div>
                <Label htmlFor="content">Email Content *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="Dear Supporter,&#10;&#10;Thank you for being part of our mission..."
                  rows={12}
                  required
                  className="font-mono text-sm"
                  data-testid="newsletter-content-input"
                />
                <p className="text-xs text-slate-500 mt-1">Plain text format. HTML support coming soon.</p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" data-testid="submit-newsletter">
                  {isEditing ? "Save Draft" : "Save as Draft"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Send Confirmation */}
        <AlertDialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Send Newsletter</AlertDialogTitle>
              <AlertDialogDescription>
                You're about to send "{selectedNewsletter?.subject}" to all your subscribers. 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleSend} disabled={sending} className="bg-green-600 hover:bg-green-700">
                {sending ? "Sending..." : "Send Newsletter"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Newsletter</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedNewsletter?.subject}"? 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
